/**
 * Display helpers for the app's contentEditable-authored rich text.
 *
 * `RichTextarea` (PrayerScreen) and `MiniEditor` (The Well) both save raw
 * `innerHTML`, so a prayer request, a calendar note and a Well entry are all
 * HTML strings. The server sanitizes them on write now, but rows predate that
 * and a display path should not depend on a backfill having reached every one —
 * so everything gets cleaned again here before it is mounted.
 *
 * This replaced a regex sanitizer local to PrayerScreen that allowed `<span>`
 * and stripped no attributes, which is how `style="background-color: rgba(0,0,0,0)"`
 * from a Google Docs paste ended up visible in a family's prayer request.
 * Attribute stripping is the whole point; keep it.
 *
 * Modelled on `sanitizeForRender` in components/well/MiniEditor.js. Kept
 * standalone rather than imported from there because that module pulls in React
 * and lucide, and a text helper should not.
 */

// Deliberately tiny: bold / italic / underline / lists / line break. Everything
// else is unwrapped (its text survives, the tag does not).
//
// SPAN and DIV are absent on purpose. Once every attribute is stripped a
// `<span>` carries no meaning, and a `<div>` is how contentEditable spells "new
// line" — both are handled before the walk, not allowed through it.
const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'UL', 'OL', 'LI', 'BR']);

// Tags whose CONTENTS must go too. Everything else is unwrapped — the tag is
// dropped and its text kept — which is right for a <span> or a <font>, and wrong
// for these: unwrapping <script>alert(1)</script> leaves `alert(1)` sitting in the
// middle of someone's prayer request as visible text. Not executable, but not
// theirs either. Found by a test, not by review.
const DROP_TAGS = new Set([
  'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'APPLET',
  'NOSCRIPT', 'TEMPLATE', 'SVG', 'MATH', 'LINK', 'META', 'TITLE', 'HEAD',
]);

/**
 * Turn contentEditable's block wrappers and any literal newlines into `<br>`.
 *
 * Both halves matter and both have bitten us:
 *
 *   - Chrome wraps each new line in `<div>`, Firefox in `<p>`. The allowlist
 *     walk below UNWRAPS unknown tags, so without this a stored line break
 *     would vanish rather than render.
 *   - Legacy values mix real newlines with HTML — a field edited before the
 *     rich editor landed, or text pasted in. Rendered as HTML those newlines
 *     collapse, which silently runs separate paragraphs together. The value
 *     that prompted this work had exactly that shape.
 */
function blocksToBreaks(html) {
  return String(html)
    .replace(/\r\n?/g, '\n')
    // Opening tag becomes the break; the closing tag just goes away. Doing it
    // the other way round puts a trailing <br> on every value.
    .replace(/<(?:div|p)\b[^>]*>/gi, '<br>')
    .replace(/<\/(?:div|p)\s*>/gi, '')
    .replace(/\n/g, '<br>');
}

/** Is this HTML string visually empty? `<br>` and `&nbsp;` alone count as empty. */
export function isHtmlBlank(html) {
  if (!html) return true;
  return !String(html).replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
}

/**
 * Clean stored rich text for `dangerouslySetInnerHTML`.
 *
 * Returns '' for blank input so callers can test the result directly rather
 * than testing the raw value and then rendering something empty.
 */
export function sanitizeForDisplay(html) {
  if (!html || isHtmlBlank(html)) return '';

  const prepared = blocksToBreaks(html);

  // No DOMParser (SSR, ancient browser): fall back to text. Returning the raw
  // HTML here would mean shipping unsanitized markup into innerHTML.
  if (typeof window === 'undefined' || !window.DOMParser) return toPlainText(html);

  const doc  = new window.DOMParser().parseFromString(`<div>${prepared}</div>`, 'text/html');
  const root = doc.body.firstChild;
  if (!root) return '';
  walk(root);

  // A leading break is an artifact of the first line's own wrapper, not
  // something the author typed.
  return root.innerHTML.replace(/^(?:<br\s*\/?>)+/i, '');

  function walk(node) {
    Array.from(node.childNodes).forEach(child => {
      if (child.nodeType !== 1) return;         // text and comments: leave alone
      if (DROP_TAGS.has(child.tagName)) {
        node.removeChild(child);                // contents and all
        return;
      }
      if (!ALLOWED_TAGS.has(child.tagName)) {
        // Unwrap: keep the words, drop the tag.
        while (child.firstChild) node.insertBefore(child.firstChild, child);
        node.removeChild(child);
        return;
      }
      Array.from(child.attributes).forEach(a => child.removeAttribute(a.name));
      walk(child);
    });
  }
}

/**
 * Reduce stored rich text to plain text, keeping line structure.
 *
 * For places that must not render markup at all — a truncated table cell, a
 * one-line summary, an aria-label. Uses the parser rather than a tag regex so
 * entities (`&amp;`, `&nbsp;`) come out as characters.
 */
export function toPlainText(html) {
  if (!html) return '';
  const withBreaks = blocksToBreaks(html).replace(/<br\s*\/?>/gi, '\n');

  let text;
  if (typeof window !== 'undefined' && window.DOMParser) {
    const doc = new window.DOMParser().parseFromString(`<div>${withBreaks}</div>`, 'text/html');
    // textContent would otherwise hand back the body of a <script> or <style> as
    // if it were prose — same trap as the unwrap above.
    doc.body.querySelectorAll([...DROP_TAGS].join(',').toLowerCase())
      .forEach(el => el.remove());
    text = doc.body.textContent || '';
  } else {
    text = withBreaks
      .replace(/<(script|style|iframe|object|embed|noscript|template)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, '')
      .replace(/<[^>]*>/g, '');
  }

  return text
    .replace(/ /g, ' ')      // nbsp → real space
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')   // collapse the runs contentEditable leaves
    .split('\n').map(l => l.trim()).join('\n')
    .trim();
}
